import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DialogTopBar } from '../components/DialogTopBar';
import {
    ArrowLeft, Plus, Trash2, Users, ClipboardCheck, Award, UserPlus, X,
    ChevronDown, ChevronRight, ChevronLeft, Zap, Check, Save, Loader2, Search,
    GraduationCap
} from 'lucide-react';
import { AttendancePanel } from '../components/AttendancePanel';
import { GradingStudentSelector } from '../components/GradingStudentSelector';

const SkillAssessmentDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { getAuthHeaders, API } = useAuth();
    const [sa, setSa] = useState(null);
    const [students, setStudents] = useState([]);
    const [allStudents, setAllStudents] = useState([]);
    const [skillForm, setSkillForm] = useState(null);
    const [grades, setGrades] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('students'); // students | attendance | grading
    const [enrollModal, setEnrollModal] = useState(false);
    const [groupModal, setGroupModal] = useState(false);
    const [groupName, setGroupName] = useState('');
    const [selectedForEnroll, setSelectedForEnroll] = useState([]);
    const [enrollSearch, setEnrollSearch] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('all');
    const [attSaving, setAttSaving] = useState(false);
    const [attSearch, setAttSearch] = useState('');
    const [panelAttendance, setPanelAttendance] = useState({});
    const [predefinedComments, setPredefinedComments] = useState([]);
    const [attComments, setAttComments] = useState({});
    const [attPredefined, setAttPredefined] = useState({});

    const fetchData = useCallback(async () => {
        try {
            const [saRes, studentsRes, commentsRes] = await Promise.all([
                axios.get(`${API}/skill-assessments/${id}`, { headers: getAuthHeaders() }),
                axios.get(`${API}/students`, { headers: getAuthHeaders() }),
                axios.get(`${API}/admin/attendance-comments`, { headers: getAuthHeaders() })
            ]);
            setSa(saRes.data);
            setAllStudents(studentsRes.data);
            setPredefinedComments(commentsRes.data);

            const enrolled = studentsRes.data.filter(s => saRes.data.enrolled_student_ids?.includes(s.id));
            setStudents(enrolled);

            if (saRes.data.skill_form_id) {
                const formRes = await axios.get(`${API}/skill-forms/${saRes.data.skill_form_id}`, { headers: getAuthHeaders() });
                setSkillForm(formRes.data);
            }

            const [gradesRes, attRes] = await Promise.all([
                axios.get(`${API}/skill-assessment-grades/${id}`, { headers: getAuthHeaders() }),
                axios.get(`${API}/skill-assessments/${id}/attendance`, { headers: getAuthHeaders() })
            ]);
            setGrades(gradesRes.data);
            setAttendance(attRes.data);

            // Build attendance maps like Lessons/Assessments
            const attMap = {}, commentMap = {}, predMap = {};
            enrolled.forEach(s => { attMap[s.id] = false; commentMap[s.id] = ''; predMap[s.id] = []; });
            attRes.data.forEach(r => { attMap[r.student_id] = r.present; commentMap[r.student_id] = r.comment || ''; predMap[r.student_id] = r.predefined_comments || []; });
            setPanelAttendance(attMap);
            setAttComments(commentMap);
            setAttPredefined(predMap);
        } catch (err) {
            toast.error('Failed to load data');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [id, API, getAuthHeaders]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleEnroll = async () => {
        if (selectedForEnroll.length === 0) return;
        try {
            await axios.post(`${API}/skill-assessments/${id}/enroll`, { student_ids: selectedForEnroll }, { headers: getAuthHeaders() });
            toast.success('Students enrolled');
            setEnrollModal(false);
            setSelectedForEnroll([]);
            fetchData();
        } catch { toast.error('Failed to enroll'); }
    };

    const handleUnenroll = async (studentId) => {
        try {
            await axios.delete(`${API}/skill-assessments/${id}/unenroll/${studentId}`, { headers: getAuthHeaders() });
            toast.success('Student removed');
            fetchData();
        } catch { toast.error('Failed to remove'); }
    };

    const handleCreateGroup = async () => {
        if (!groupName.trim()) return;
        try {
            await axios.post(`${API}/skill-assessments/${id}/groups`, { name: groupName.trim() }, { headers: getAuthHeaders() });
            toast.success('Group created');
            setGroupModal(false);
            setGroupName('');
            fetchData();
        } catch { toast.error('Failed to create group'); }
    };

    const handleDeleteGroup = async (groupId) => {
        if (!window.confirm('Delete this group?')) return;
        try {
            await axios.delete(`${API}/skill-assessments/${id}/groups/${groupId}`, { headers: getAuthHeaders() });
            toast.success('Group deleted');
            if (selectedGroup === groupId) setSelectedGroup('all');
            fetchData();
        } catch { toast.error('Failed to delete group'); }
    };

    const handleAddToGroup = async (groupId, studentId) => {
        try {
            await axios.post(`${API}/skill-assessments/${id}/groups/${groupId}/students`, { student_id: studentId }, { headers: getAuthHeaders() });
            toast.success('Added to group');
            fetchData();
        } catch { toast.error('Failed'); }
    };

    const handleRemoveFromGroup = async (groupId, studentId) => {
        try {
            await axios.delete(`${API}/skill-assessments/${id}/groups/${groupId}/students/${studentId}`, { headers: getAuthHeaders() });
            toast.success('Removed from group');
            fetchData();
        } catch { toast.error('Failed'); }
    };

    // Attendance - consistent card-based approach
    const togglePredefined = (sid, label) => {
        setAttPredefined(prev => {
            const c = prev[sid] || [];
            return { ...prev, [sid]: c.includes(label) ? c.filter(l => l !== label) : [...c, label] };
        });
    };

    const saveAttendance = async () => {
        setAttSaving(true);
        try {
            const records = students.map(s => ({
                student_id: s.id, present: panelAttendance[s.id] || false,
                comment: attComments[s.id] || '', predefined_comments: attPredefined[s.id] || []
            }));
            await axios.post(`${API}/skill-assessments/${id}/attendance`, { records }, { headers: getAuthHeaders() });
            toast.success('Attendance saved');
        } catch { toast.error('Failed to save attendance'); }
        finally { setAttSaving(false); }
    };

    // Sort by year then surname helper
    const sortBySurname = (list) => [...list].sort((a, b) => {
        if ((a.year || 0) !== (b.year || 0)) return (a.year || 0) - (b.year || 0);
        const surnameA = (a.name || '').split(' ').pop().toLowerCase();
        const surnameB = (b.name || '').split(' ').pop().toLowerCase();
        return surnameA.localeCompare(surnameB);
    });

    // Grading
    const saveGrade = async (studentId, gradeData) => {
        try {
            await axios.post(`${API}/skill-assessment-grades`, {
                skill_assessment_id: id,
                student_id: studentId,
                grades: gradeData
            }, { headers: getAuthHeaders() });
            toast.success('Grade saved');
            fetchData();
        } catch { toast.error('Failed to save grade'); }
    };

    if (loading) {
        return <Layout><div className="space-y-4 page-enter">{[1,2,3,4].map(i => <div key={i} className="skeleton h-16 rounded-xl" />)}</div></Layout>;
    }

    if (!sa) {
        return <Layout><div className="text-center py-20"><p className="text-gray-500">Skill assessment not found</p></div></Layout>;
    }

    const localGroups = sa.local_groups || [];
    const filteredStudents = sortBySurname(
        selectedGroup === 'all'
            ? students
            : selectedGroup === 'unassigned'
                ? students.filter(s => !localGroups.some(g => g.student_ids?.includes(s.id)))
                : students.filter(s => localGroups.find(g => g.id === selectedGroup)?.student_ids?.includes(s.id))
    );

    const unenrolledStudents = sortBySurname(
        allStudents.filter(s => !sa.enrolled_student_ids?.includes(s.id) && s.year === sa.year)
    );

    const filteredUnenrolled = enrollSearch
        ? unenrolledStudents.filter(s => s.name.toLowerCase().includes(enrollSearch.toLowerCase()) || s.student_id.toLowerCase().includes(enrollSearch.toLowerCase()))
        : unenrolledStudents;


    return (
        <Layout>
            <div className="space-y-6 page-enter" data-testid="skill-assessment-detail">
                {/* Header */}
                <div className="flex items-start gap-3">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/skill-assessments')}
                        className="mt-1 text-slate-500 hover:text-ica-navy dark:text-slate-400 dark:hover:text-ica-gold" title="Back">
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="flex-1">
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                            <span className="hover:text-ica-navy cursor-pointer" onClick={() => navigate('/skill-assessments')}>Skill Assessments</span>
                            <span>/</span>
                            <span className="text-gray-900 font-medium">{sa.title}</span>
                        </div>
                        <h1 className="font-heading text-3xl font-bold text-ica-navy">{sa.title}</h1>
                        {sa.description && <p className="text-gray-500 mt-1">{sa.description}</p>}
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span>Year {sa.year}</span>
                            <span>{sa.date}</span>
                            {skillForm && <span className="bg-ica-gold/10 text-ica-gold px-2 py-0.5 rounded-full text-xs font-medium">{skillForm.name}</span>}
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 bg-gray-100 dark:bg-slate-700 rounded-lg p-1 w-fit">
                    {[
                        { key: 'students', label: 'Students', icon: Users },
                        { key: 'attendance', label: 'Attendance', icon: ClipboardCheck },
                        { key: 'grading', label: 'Grading', icon: Award }
                    ].map(t => (
                        <button key={t.key} onClick={() => setTab(t.key)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === t.key ? 'bg-white dark:bg-slate-800 text-ica-navy dark:text-ica-light-gold shadow-sm' : 'text-gray-500 dark:text-slate-400 hover:text-gray-700'}`}
                            data-testid={`tab-${t.key}`}>
                            <t.icon className="w-4 h-4" />{t.label}
                        </button>
                    ))}
                </div>

                {/* LOCAL GROUPS */}
                <div className="flex flex-wrap gap-2 items-center">
                    <button onClick={() => setSelectedGroup('all')}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedGroup === 'all' ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                        All ({students.length})
                    </button>
                    <button onClick={() => setSelectedGroup('unassigned')}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedGroup === 'unassigned' ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                        Unassigned
                    </button>
                    {localGroups.map(g => (
                        <div key={g.id} className="flex items-center gap-1">
                            <button onClick={() => setSelectedGroup(g.id)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedGroup === g.id ? 'bg-ica-gold text-white' : 'bg-ica-gold/10 text-ica-gold hover:bg-ica-gold/20'}`}>
                                {g.name} ({g.student_ids?.length || 0})
                            </button>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                                onClick={() => handleDeleteGroup(g.id)} title="Delete Group">
                                <X className="w-3 h-3" />
                            </Button>
                        </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => setGroupModal(true)} title="Add Local Group">
                        <Plus className="w-3.5 h-3.5 mr-1" /> Group
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setEnrollModal(true)} title="Enroll Students">
                        <UserPlus className="w-3.5 h-3.5 mr-1" /> Enroll
                    </Button>
                </div>

                {/* TAB: STUDENTS */}
                {tab === 'students' && (
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        {filteredStudents.length === 0 ? (
                            <div className="p-12 text-center text-gray-500">No students in this view. Enroll students to get started.</div>
                        ) : (
                            <table className="w-full ica-table">
                                <thead>
                                    <tr>
                                        <th className="px-6 py-3 text-left">Student</th>
                                        <th className="px-6 py-3 text-left">ID</th>
                                        <th className="px-6 py-3 text-left">Local Group</th>
                                        <th className="px-6 py-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filteredStudents.map(s => {
                                        const studentGroup = localGroups.find(g => g.student_ids?.includes(s.id));
                                        return (
                                            <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-3 font-medium text-gray-900">{s.name}</td>
                                                <td className="px-6 py-3 text-sm text-gray-600">{s.student_id}</td>
                                                <td className="px-6 py-3">
                                                    {studentGroup ? (
                                                        <span className="bg-ica-gold/10 text-ica-gold px-2 py-0.5 rounded-full text-xs font-medium">{studentGroup.name}</span>
                                                    ) : (
                                                        <select className="text-xs border rounded px-2 py-1"
                                                            value="" onChange={e => e.target.value && handleAddToGroup(e.target.value, s.id)}>
                                                            <option value="">Assign to group...</option>
                                                            {localGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                                        </select>
                                                    )}
                                                </td>
                                                <td className="px-6 py-3 text-right">
                                                    <div className="flex items-center justify-end gap-1">
                                                        {studentGroup && (
                                                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500 hover:text-ica-navy dark:text-slate-400"
                                                                onClick={() => handleRemoveFromGroup(studentGroup.id, s.id)} title="Remove from Group">
                                                                <X className="w-3.5 h-3.5" />
                                                            </Button>
                                                        )}
                                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                                                            onClick={() => handleUnenroll(s.id)} title="Remove Student">
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {/* TAB: ATTENDANCE */}
                {tab === 'attendance' && (
                    <AttendancePanel
                        students={filteredStudents}
                        groups={[]}
                        search={attSearch}
                        onSearch={setAttSearch}
                        predefinedComments={predefinedComments}
                        isPresent={(sid) => panelAttendance[sid]}
                        onSetPresent={(sid, val) => setPanelAttendance(p => ({ ...p, [sid]: val }))}
                        predefinedFor={(sid) => attPredefined[sid] || []}
                        onTogglePredefined={(sid, label) => togglePredefined(sid, label)}
                        commentFor={(sid) => attComments[sid] || ''}
                        onSetComment={(sid, val) => setAttComments(p => ({ ...p, [sid]: val }))}
                        onSave={saveAttendance}
                        saving={attSaving}
                    />
                )}

                {/* TAB: GRADING */}
                {tab === 'grading' && (
                    <GradingTab students={filteredStudents} skillForm={skillForm} grades={grades} saveGrade={saveGrade} />
                )}

                {/* Enroll Modal */}
                <Dialog open={enrollModal} onOpenChange={(open) => { setEnrollModal(open); if (!open) setEnrollSearch(''); }}>
                    <DialogContent className="max-w-lg" data-testid="enroll-modal">
                        <DialogTopBar title={<>Enroll Students</>}>
    <Button size="sm" variant="outline" onClick={() => setEnrollModal(false)}>Cancel</Button>
                            <Button size="sm" onClick={handleEnroll} disabled={selectedForEnroll.length === 0} data-testid="confirm-enroll-btn">
                                <UserPlus className="w-4 h-4 mr-2" /> Enroll ({selectedForEnroll.length})
                            </Button>
</DialogTopBar>
                        <div className="space-y-4 py-2">
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <Input value={enrollSearch} onChange={e => setEnrollSearch(e.target.value)} placeholder="Search students..." className="pl-10" data-testid="enroll-search" />
                            </div>
                            {filteredUnenrolled.length > 0 && (
                                <Button variant="outline" className="w-full" onClick={() => { setSelectedForEnroll(filteredUnenrolled.map(s => s.id)); }} data-testid="select-all-btn">
                                    <Users className="w-4 h-4 mr-2" /> Select All Year {sa.year} ({filteredUnenrolled.length})
                                </Button>
                            )}
                            <div className="max-h-64 overflow-y-auto space-y-1">
                                {filteredUnenrolled.length === 0 ? (
                                    <p className="text-sm text-gray-400 text-center py-4">
                                        {enrollSearch ? 'No matching students' : `All Year ${sa.year} students are enrolled`}
                                    </p>
                                ) : filteredUnenrolled.map(s => (
                                    <label key={s.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                                        <input type="checkbox" checked={selectedForEnroll.includes(s.id)}
                                            onChange={e => setSelectedForEnroll(prev => e.target.checked ? [...prev, s.id] : prev.filter(x => x !== s.id))}
                                            className="rounded border-gray-300" />
                                        <span className="text-sm font-medium">{s.name}</span>
                                        <span className="text-xs text-gray-400 ml-auto">{s.student_id}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Create Group Modal */}
                <Dialog open={groupModal} onOpenChange={setGroupModal}>
                    <DialogContent className="max-w-sm" data-testid="group-modal">
                        <DialogTopBar title={<>Create Local Group</>}>
    <Button size="sm" variant="outline" onClick={() => setGroupModal(false)}>Cancel</Button>
                            <Button size="sm" onClick={handleCreateGroup} data-testid="confirm-group-btn">Create</Button>
</DialogTopBar>
                        <p className="text-xs text-gray-500">This group is temporary and only exists within this skill assessment. It won't affect global groups.</p>
                        <Input value={groupName} onChange={e => setGroupName(e.target.value)} placeholder="Group name" data-testid="group-name-input"
                            onKeyDown={e => e.key === 'Enter' && handleCreateGroup()} />
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};

// ========== Grading Tab Component ==========
const GradingTab = ({ students, skillForm, grades, saveGrade }) => {
    const [currentId, setCurrentId] = useState(null);
    useEffect(() => {
        if (students.length > 0 && !students.some(s => s.id === currentId)) {
            setCurrentId(students[0].id);
        }
    }, [students, currentId]);

    if (!skillForm) {
        return (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <Award className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No skill form assigned. Assign a published skill form to enable grading.</p>
            </div>
        );
    }

    if (students.length === 0) {
        return <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-500">No students in this view</div>;
    }

    const current = students.find(s => s.id === currentId) || students[0];
    const gradedStudentIds = grades.filter(g => Object.keys(g.grades || {}).length > 0).map(g => g.student_id);
    const studentGrade = grades.find(g => g.student_id === current.id);

    return (
        <div className="space-y-4" data-testid="grading-tab">
            <GradingStudentSelector
                students={students}
                groups={[]}
                currentStudentId={current.id}
                onSelectStudent={setCurrentId}
                gradedStudentIds={gradedStudentIds}
            />
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <StudentGradingForm key={current.id} student={current} skillForm={skillForm} existingGrades={studentGrade?.grades || {}} saveGrade={saveGrade} />
            </div>
        </div>
    );
};

// ========== Individual Student Grading Form ==========
const StudentGradingForm = ({ student, skillForm, existingGrades, saveGrade }) => {
    const [localGrades, setLocalGrades] = useState(existingGrades);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const dirtyRef = React.useRef(false);
    const gradesRef = React.useRef(existingGrades);

    const setGradeValue = (criterionId, value) => {
        setLocalGrades(prev => {
            const newGrades = { ...prev };
            if (newGrades[criterionId] === value) {
                delete newGrades[criterionId]; // Toggle off
            } else {
                newGrades[criterionId] = value;
            }
            gradesRef.current = newGrades;
            return newGrades;
        });
        setDirty(true);
        dirtyRef.current = true;
    };

    const handleSave = async () => {
        setSaving(true);
        await saveGrade(student.id, localGrades);
        setDirty(false);
        dirtyRef.current = false;
        setSaving(false);
    };

    // Auto-save on unmount (when collapsing)
    useEffect(() => {
        return () => {
            if (dirtyRef.current) {
                saveGrade(student.id, gradesRef.current);
            }
        };
    }, [student.id, saveGrade]);

    return (
        <div className="border-t border-gray-100 px-5 py-4 space-y-4">
            {/* Save button at top */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {dirty && <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" title="Unsaved changes" />}
                    <span className="text-xs text-gray-400">{dirty ? 'Unsaved changes — auto-saves on collapse' : 'All changes saved'}</span>
                </div>
                <Button size="sm" onClick={handleSave} disabled={!dirty || saving} data-testid={`save-grade-${student.id}`}>
                    {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-1" /> Save Grades</>}
                </Button>
            </div>

            {skillForm.skills.map(skill => (
                <div key={skill.id}>
                    <p className="text-sm font-bold text-ica-navy mb-2">{skill.name}</p>
                    <div className="space-y-2 ml-2">
                        {skill.criteria.map(crit => (
                            <div key={crit.id} className="flex items-center gap-3">
                                <span className="text-sm text-gray-700 flex-1 min-w-0">{crit.name}</span>
                                <div className="flex gap-1.5 flex-shrink-0">
                                    {skillForm.grading_scale.map(grade => (
                                        <button key={grade} onClick={() => setGradeValue(crit.id, grade)}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border-2 ${localGrades[crit.id] === grade
                                                ? 'bg-ica-gold border-ica-gold text-white shadow-sm'
                                                : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'}`}
                                            data-testid={`grade-${student.id}-${crit.id}-${grade}`}>
                                            {grade}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default SkillAssessmentDetailPage;
